import type { Geometry } from 'geojson';
import { Boundary } from '@tcc/shared-data-lib';

// @todo: Improve this file iteratively
export type Zone = {
  meta: {
    isDeadZone: boolean;
    highestYield: {
      estimatedProfit: {
        q: number;
        u: 'usd/hectare';
      };
      estimatedYield: {
        q: number;
        u: 'kg/hectare';
      };
      rate: {
        q: number;
        u: 'seed/hectare';
      };
    };
    applications: {
      fertilizer: Array<unknown>;
      seed: Array<{
        'estimated-profit': {
          q: number;
          u: 'usd/acre';
        };
        'estimated-yield': {
          q: number;
          u: 'bu/ac';
        };
        product: {
          id: number;
          name: string;
          brand: string;
        };
        rate: {
          q: number;
          u: 'seed/acre';
        };
        'target-yield': {
          q: number;
          u: 'bu/ac';
        };
      }>;
    };
    area: {
      q: number;
      u: 'acres';
    };
    geometry: Geometry;
    index: number;
    zoneId: string;
    inputState: string;
  };
};

export type Product = {
  'updated-at': string;
  'crop-uuid': string;
  name: string;
  'legacy-id': number;
  generic: boolean;
  'brand-name': string;
  'brand-uuid': string;
  uuid: string;
  rm: {
    q: string;
    u: 'days';
  };
  id: string;
  brand: string;
};

export type Script = {
  activity: 'planting' | 'application-fungicide' | 'application-insecticide' | 'application-mix';
  'activity-uuid': string;
  bases: Array<{ name: string }>;
  'cropping-year': string;
  'create-time': string;
  'update-time': string;
  'field-id': number;
  'field-uuid': string;
  'fmz-id': string;
  'fmz-name': string;
  'fmz-version': number;
  id: string;
  name: string;
  primary: boolean;
  season: string;
  tag: string;
  warnings: string[];
  attributes: {
    crop: string;
    id: number;
  };
  summary: {
    zones: Array<{
      id: string;
      area: {
        q: number;
        u: 'acre' | 'hectare';
      };
    }>;
  };
  meta: {
    fieldId: string;
    name: string;
    products: Product[];
    scriptState: string;
    crop: string;
    cropId: number;
  };
  zones: {
    [id: string]: Zone;
  };
  highestEconomicField: {
    estimatedProfit: {
      q: number;
      u: 'usd/hectare';
    };
    estimatedYield: {
      q: number;
      u: 'kg/hectare';
    };
    rate: {
      q: number;
      u: 'seed/hectare';
    };
  };
  densityCurveField: {
    hardinParams: {
      hardinParamsA: number;
      hardinParamsB: number;
    };
  };
};

export type Scenario = {
  productName: string;
  //currently scenarios are valid only for corn, this can be a union in the future
  crop: 'corn';
  product: {
    id: number | string;
    name: string;
    brand: string;
  };
  area: {
    q: number;
    u: 'acre';
  };
  avgRate: {
    q: number;
    u: 'seed/acre';
  };
  avgTargetYield: {
    q: number;
    u: 'bu/ac';
  };
  numSeedBags: {
    q: number;
    u: 'bag';
  };
  avgEstYield: {
    q: number;
    u: 'bu/ac';
  };
  avgEstGrossRevenue: {
    q: number;
    u: 'usd/acre';
  };
  costSeedPerAcre: {
    q: number;
    u: 'usd/acre';
  };
  meta: {
    fieldId: string;
    name: string;
    products: Product[];
    scriptState: 'INSYNC' | string;
    crop: string;
    cropId: number;
  };
  plantingPlanParams: {
    brand: string;
    'goal-ratio': string;
    hasPlantingFertilizerApplication: boolean;
    products: {
      seed: unknown;
      fertilizer: unknown;
      application: unknown;
      APPLICATION_v2: unknown[];
    };
    date: string;
    agronomicSeason: string;
    activityCategoryUuid: string;
    activityType: string;
    countryCode: string;
    activityUuid: string;
    seasonCode: string;
    defaultScriptName: string;
    isDensityAdjustmentEnabled: boolean;
    productId: number;
    currentAveragePopulation: string;
    'target-yield': string;
    'price-product': string;
    'price-commodity': string;
  };
  scenarioUuid: string;
  seedChartData: {
    areaValues: number[];
    seedRateBins: Array<[number, number]>;
  };
  zones: {
    [id: string]: Zone;
  };
  tag: 'auto';
  densityCurveField: {
    hardinParams: {
      hardinParamsA: number;
      hardinParamsB: number;
    };
    estimatedYield: {
      q: number;
      u: string;
    };
  };
};

export type Current = {
  cropId: number;
  scriptId?: string;
  fatalError?: {
    message: string;
  };
  fieldUuid?: string;
};

export type ExportParams = {
  scopeType: 'field' | 'operation';
  scriptType: string;
  operationUuid: string;
  primaryOnly: boolean;
  scripts?: number;
};

export type FieldByUuid = {
  [uuid: string]: Field;
}

export enum SeasonName {
  SAFRINHA = 'SAFRINHA',
  SPRING = 'SPRING',
  SUMMER = 'SUMMER',
  FALL = 'FALL',
  WINTER = 'WINTER'
}

type BaseLayer = {
  eventDate: string;
  id: string;
  isUsable: boolean;
  season: SeasonName;
  year: number;
};

export type YieldLayer = BaseLayer & {
  cropId: string;
  averageYield?: {
    u: string;
    v: number | null;
  };
};

export type CCILayer = BaseLayer & {
  imageryThumbnailUrl: string;
};

type AvailableLayers = {
  cci: {
    [year: string]: {
      [month: string]: CCILayer[];
    };
  };
  yield: {
    [cropId: string]: YieldLayer[];
  };
};

export type ManualLayers = {
  [fieldUuid: string]: AvailableLayers | { error: true };
}

export type SelectedLayers = (CCILayer | YieldLayer)[]

export type Bins = number[][];

export type Store = {
  affiliates: Affiliates;
  scenarios: {
    allScenarios: Scenario[];
    activeScenarioUuid: string;
    originalScenarioUuid: string;
    yourPlanScenarioUuid: string;
  };
  scripts: {
    [id: string]: Script;
  };
  crops: {
    [id: number]: Crop;
    [uuid: string]: Crop;
  };
  user: User;
  current: Current;
  farms: {
    [uuid: string]: Farm;
  };
  launchDarkly: Record<string, boolean>;
  devices: {
    drive: Drive[];
  };
  fields: {
    byId: {
      [id: number]: Field;
    };
    byUuid: FieldByUuid;
  };
  operations: {
    [id: string]: Operation;
  };
  exportSelector: {
    params?: ExportParams;
    isFvFieldSelected: { [key: string]: boolean };
    options?: object;
    rawScriptList: Script[];
  };
  manual: {
    layers: ManualLayers;
    generateZones: {
      selectedLayerGroup?: string;
      selectedLayers: SelectedLayers;
      zones: Record<string, GeneratedZone>;
    };
  };
  productFeatures: {
    isEligibleProductsFetchCompleted: boolean;
    eligibleProducts: {
      [productName: string]: {
        features: string[];
      };
    };
    byUuid: {
      [fieldUuid: string]: {
        [featureName: string]: boolean;
      };
    };
  };
  uiwidgets: {
    isSidePanelOpen: boolean;
    isExportModalOpen: boolean;
    isScenarioPlannerOpen: boolean;
    productRateLegend?: {
      bins: Bins;
    };
  };
};

export type Crop = {
  'updated-at': string;
  'nominal-weight': {
    q: number;
    u: 'pound/bushel';
  };
  name: string;
  'legacy-id': number;
  source: {
    code: string;
    id: string;
  };
  'product-type': string;
  code: string;
  uuid: string;
  'dfp-uuid': string;
  id: number;
};

export type CropFromOL = {
  crops: {
    nodes: {
      code: string;
      id: string;
      name: string;
      nominalWeight: {
        value: number;
        unit: 'pound/bushel';
      };
      cropLegacyIds: {
        cropCode: string;
        id: string;
      };
    }[];
  };
}

export interface ValidationRange {
  min: number;
  max: number;
  unit?: string;
  type?: string;
  step?: number;
}

interface BagSize {
  quantity: number;
  unit: 'seed';
}

interface RM {
  min: number;
  max: number;
  step?: number;
}

interface CropValidation {
  CROP_ID: number;
  BAG_SIZE: BagSize;
  RATE: ValidationRange;
  TARGET_YIELD: ValidationRange;
  SEED_COST?: ValidationRange;
  GRAIN_PRICE?: ValidationRange;
  RM: RM;
}

interface ApplicationMethod {
  RATE: ValidationRange;
}

interface ComponentValidation {
  DRY?: ApplicationMethod;
  WET?: ApplicationMethod;
}

export interface ProductValidationValues {
  COUNTRY: string;
  PLANTING: {
    CORN: CropValidation;
    SOYBEANS: CropValidation;
  };
  FERTILITY: {
    LPK: ComponentValidation;
    NITROGEN: ComponentValidation;
    FERTILIZER: {
      RATE: ValidationRange;
    };
  };
  APPLICATION: {
    LPK: ComponentValidation;
    APPLICATION: {
      RATE: ValidationRange;
    };
  };
}

// This type can be improve as we use it
export type User = {
  currency: string;
  unitsOfMeasureTemplate: string;
  uuid: string;
  email: string;
  productValidationValues: ProductValidationValues;
};

export type Farm = {
  growerName: string;
  updatedAt: string;
  permission: string;
  createdBy: number;
  name: string;
  createdAt: string;
  userUuid: string;
  updatedBy: number;
  id: string;
  userId: number;
  enabled: boolean;
};

export type Organization = {
  id: string;
  name: string;
  sync: boolean;
  connection_url: string | null;
  rca_url: string | null;
};

export type WorkPlanBase = {
  scriptId: string;
  fieldUuid: string;
  jdFieldId: string;
  createdAt: string;
  updatedAt: string;
};

export type WorkPlanPending = {
  id: null;
  url: null;
  state: 'PENDING';
};

export type WorkPlanComplete = {
  id: string;
  url: string;
  state: 'COMPLETE';
};

export type JohnDeereField = {
  id: string;
  fvField?: string;
  name: string;
  organizationId: string;
  clientName: string;
  fvFieldUuid?: string;
};

export type JohnDeereClient = {
  id: string;
  name: string;
};

export type WorkPlan = WorkPlanBase & (WorkPlanPending | WorkPlanComplete)

export type WorkPlans = {
  byScriptId?: Partial<{
    [id: string]: WorkPlan;
  }>;
}

export type AffiliateFields = {
  byId: Partial<{
    [id: string]: JohnDeereField;
  }>;
  ids: string[];
  byClient: Partial<{
    [clientName: string]: string[];
  }>;
}

export type AffiliateClients = {
  ids: string[];
  byName: Partial<{
    [clientName: string]: JohnDeereClient;
  }>;
}

export type Affiliate = {
  organizations: Organization[];
  selectedOrganization: string;
  workPlans: WorkPlans;
  fields: AffiliateFields;
  clients: AffiliateClients;
  selectedField: string;
}

export type Affiliates = {
  connections: string[];
  deere?: Affiliate;
  index?: string;
};

export type Drive = {
  name: string;
  description: string;
  id: string;
  deviceModelId: string;
};

export type FieldArea = {
  q: number;
  u: string;
}

export type FieldFarm = {
  id: string;
  growerName: string;
  name: string;
}

export type Field = {
  uuid: string;
  id: string;
  name: string;
  farmId: string;
  area: FieldArea;
  centroid: {
    coordinates: [number, number];
    type: string;
  };
  farm: FieldFarm;
  countryCode: string;
  operationId: string;
  operationName: string;
  boundary: Boundary;
};

export type Operation = {
  operationId: string;
  operationName: string;
  operationOwnerId: number;
  permission: string;
}

export type YieldPerCrop = {
  crop: {
    id: string;
  };
  averageYield: {
    v: number;
    u: string;
  };
}

export type ZoneProperties = {
  isDeadZone: boolean;
  relativeProductivity?: {
    v: number | null;
    u: 'percentage';
  };
  yieldPerCrop?: YieldPerCrop[];
  averageCCI?: {
    v: number | null;
    u: string;
  };
};

type GeoJsonData = {
  type: 'Feature';
  geometry: Geometry;
  properties: ZoneProperties;
}

type ZoneMetadata = {
  area: { q: number; u: string };
  geometry: Geometry;
  index: number;
  zoneId: string;
};

export type GeneratedZone = {
  geoJson: GeoJsonData;
  meta: ZoneMetadata;
}

export interface Activity {
  date: string;
  'field-uuid': string;
  primary?: string | null;
  'update-time'?: string;
  type: string;
  season: string;
  'create-time'?: string;
  uuid: string;
  attributes?: unknown;
  enabled?: boolean;
}
